// What the Accelerator's own pages say, worked out from its rows (data.js) and the app's clock. Pure:
// no network, no React, nothing but format.js, copy.js, the accountability log's own pure words (src/products/
// mastermind/log.js) and the product pages' pure model (src/product/model.js), so a test can import it as it is.
//
// - The goal: the member drafts it, Adam locks it ("Locked 30 Aug"); the month of the 12-month term
//   (hub.my_mastermind: payments taken for a Stripe subscription, calendar months otherwise) and its
//   12-segment bar. Past month 12 the bar stays full and the line says so; nothing ends on its own.
// - The next session: their group's or every group's next one, from its start to its end ("5pm to 8:30pm on Zoom,
//   Group 2", the critique's member 14), Join from join_opens_minutes before it starts until it ends (the Library's
//   rule), Add to calendar, and their attendance from their own accountability log, worded as it words it ("2 of 3
//   sessions attended live": a session caught up on the recording still counts as missed, and excused, paused and
//   unrecorded sessions don't count; member 1, 20 Oct), with where they stand, which the pill opens. Nothing of it
//   shows until the log has loaded, and none if it doesn't. Off track, the line under the pill says why, as plain
//   facts in the member's log words; on track, that they are. While the refocus card shows, the card says the
//   verdict, so the next session has no pill and no line (member 3). It never names the guarantee, its rules or its
//   limits, and Adam's own words for the guarantee never reach the member (Adam, 18 Oct: "Members don't need to see
//   the rules; it's in the terms of service"). Whether members see the pill and its line is each mastermind's own
//   setting (catalogue.settings.guarantee.shown, an internal key): guaranteeOf says whether.
// - This month's actions: the open cycle's, ticked as they go, each carried-over 1 with the month it
//   came from; past months: the closed cycles, newest first.
// - Wins: newest first, each with its kind and date ("Marketing, 14 Sep"; "today" for today's), its
//   "More about it" (Adam's yes of 11 Oct: copied wins keep theirs, and members add and change it), and
//   what Edit starts from (its words, its kind, its more about it and whether Adam may share it). Edit
//   and Delete are on the wins the member logged; a win staff logged has only its share tick (Adam, 7 Oct).
import * as f from '../../lib/format.js'
import { COPY, categoryName } from './copy.js'
import { LIMITS } from './limits.js'
// where they stand, in the accountability log's own words (pure, as this is)
import { accountability, standLines, wdm } from '../mastermind/log.js'
import { LOG, LOG_MEMBER, REFOCUS, causeOf } from '../mastermind/logWords.js'
import { isReleased, progressFor } from '../../product/model.js'

const MIN = 60e3
const zoomLink = url => (typeof url === 'string' && /^https:\/\//.test(url) ? url : null)

// Their standing on this product (hub.my_mastermind's row for it), or null
export function standingFor(rows, productId) {
  return (rows || []).find(r => r.product_id === productId) || null
}

// The goal card: { state: 'none' | 'draft' | 'locked', body, locked, side, monthLine, bar, intro }
export function goalView(goal, standing) {
  const months = standing?.term_months || 12
  const month = standing?.term_month || null
  const monthLine = month ? (month > months ? COPY.pastTerm(month, months) : COPY.monthOf(month, months)) : null
  const filled = month ? Math.min(month, months) : 0
  const bar = Array.from({ length: months }, (_, i) => i < filled)
  const base = { monthLine, bar, months }
  // a session of their group has been held for them (on the register or not), so the goal is agreed
  // at the next one, not the first: an unlocked goal, or a draft left since the first session
  const held = ['attended', 'missed', 'excused', 'unrecorded'].reduce((n, k) => n + (standing?.[k] || 0), 0) > 0
  if (!goal) return { ...base, state: 'none', body: '', intro: COPY.noGoal(months, held), side: null }
  if (goal.status === 'locked') {
    return { ...base, state: 'locked', body: goal.body, locked: COPY.lockedOn(f.dayMonth(goal.locked_at)), side: COPY.setWithAdam }
  }
  return { ...base, state: 'draft', body: goal.body, side: COPY.draftLine(held) }
}

// The line for the database's refusal of a goal, or the general one
export function goalError(error) {
  if (error?.message === 'goal_locked') return COPY.goalLocked
  if (error?.code === '23514') return COPY.goalLength
  return null
}

// The next session they can see that hasn't ended, or null
export function nextSession(sessions, nowMs) {
  return (sessions || []).filter(s => s.status !== 'cancelled' && s.starts_at
    && Date.parse(s.starts_at) + (s.duration_minutes || 0) * MIN > nowMs)
    .sort((a, b) => Date.parse(a.starts_at) - Date.parse(b.starts_at))[0] || null
}

// Whether a mastermind's members see where they stand on its home (catalogue.settings.guarantee.shown, the
// product-types follow-ups, 13 Oct): null when it has none or it's switched off (shown: false), else {}. Only whether:
// the setting's words (line) and numbers are Adam's and never reach the member (Adam, 18 Oct). The Property
// Accelerator's setting came with its move, so it shows as it always has. A mastermind made in Add a product has none,
// and a copy's is switched off (admin/products/model.js duplicateRow), until it's switched on in its settings.
export function guaranteeOf(product) {
  const g = product?.settings?.guarantee
  if (!g || typeof g !== 'object' || g.shown === false) return null
  return {}
}

// The attendance flag under the pill: the sessions missed and whether the recordings were watched, as plain facts
// ("2 sessions missed and the recordings not watched."), or null with none missed
export function missedNote(log) {
  const se = accountability(log).sessions
  const missed = se.missed + se.late
  return missed ? LOG_MEMBER.missed(missed, se.late) : null
}

// Attendance and where they stand: { line, pill: { key, text, href } | null, note }, or null with no standing or no
// log. log: their accountability log (hub.my_accountability_log), read with the page: the attendance is its own count,
// in its own words ("2 of 3 sessions attended live", member 1, 20 Oct), and the pill its state. Off track, the line
// under the pill is the log's reasons as plain facts (whichever measure it is, so it never contradicts the pill) with
// Adam's push; on track, that they are. Never a rule, a limit or what 1 more miss would do (Adam, 18 Oct). guarantee:
// guaranteeOf(product); switched off (null), the attendance line alone. refocus: the refocus card is showing, so it
// says the verdict and here there's no pill and no line under it (member 3).
export function standingView(standing, log = null, href = null, guarantee = {}, { refocus = false } = {}) {
  if (!standing || !log) return null
  const acc = accountability(log)
  const se = acc.sessions
  // every session so far excused: nothing counted yet, so no "0 of 0" (Adam's answer 11 of 7 Oct)
  const line = se.counted
    ? COPY.attendedLive(se.live, se.counted) + (se.excused ? COPY.excused(se.excused) : '')
    : se.excused ? COPY.allExcused(se.excused) : COPY.noRegister
  if (!guarantee || refocus) return { line, pill: null, note: null }
  const key = acc.state
  const pill = COPY.logState[key] ? { key, text: COPY.logState[key], href } : null
  // the log's own lines under the pill: what's slipped and Adam's push, or On track's (levels.js, Adam, 18 Oct, 15:45)
  const note = key !== 'getting_started' ? standLines(acc.level, acc).slice(0, 2).join(' ') : null
  return { line, pill, note }
}

// The actions carried into this month and not ticked yet, from their log (its open month), named on the refocus card
// (member 6, 20 Oct). The home names them from its own rows instead, so a tick there shows at once.
export function carriedFromLog(log) {
  const open = (log?.months || []).find(m => !m.closed_at && !m.after_term)
  return (open?.actions || []).filter(a => a.result !== 'set_aside' && a.hops > 0 && !(a.ticked || a.done_at)).map(a => a.body)
}

// The first reviewed month with an action not done, by month: the month "See which in your log" opens (member 6)
export function firstNotDoneMonth(log) {
  const months = (log?.months || []).filter(m => m.closed_at && !m.after_term).slice().sort((a, b) => a.month - b.month)
  const m = months.find(x => (x.actions || []).some(a => a.result !== 'set_aside' && a.result !== 'done'))
  return m?.cycle_id || null
}

// The latest session they missed live with its recording still not watched, from their log, when that recording is open
// to them (member 9, 20 Oct): { day: 'Sun 27 Sep', href } or null. Watched since the log was read (their progress
// says so) is watched.
export function missedToWatch(log, data, product) {
  const all = [...(log?.before_months || []), ...(log?.months || []).flatMap(m => m.sessions || [])]
  const missed = all.filter(x => x.result === 'missed' && !x.caught_up_on && x.session_id)
    .sort((a, b) => Date.parse(b.starts_at) - Date.parse(a.starts_at))
  for (const x of missed) {
    const rec = (data?.recordings || []).find(r => r.session_id === x.session_id && r.key && isReleased(data, r.id))
    if (!rec) continue
    if (progressFor(data, rec.id)?.watched_at) return null
    return { day: wdm(x.starts_at), href: `${product?.path || ''}/${rec.key}`, sessionId: x.session_id, recordingId: rec.id }
  }
  return null
}

// The sessions they missed live with the recording not watched, from their log, as a set of session ids, for the
// "You missed this one" marks on Sessions and Recordings (member 9)
export function missedUnwatched(log) {
  const all = [...(log?.before_months || []), ...(log?.months || []).flatMap(m => m.sessions || [])]
  return new Set(all.filter(x => x.result === 'missed' && !x.caught_up_on && x.session_id).map(x => x.session_id))
}

// The refocus card (Adam, 18 Oct, 16:12), only when they're A little behind, Behind or Well behind: { key, heading,
// goal, only, facts, carried, which, askH, asks, help } or null. log: their accountability log; goal: their goal row
// (locked or a draft), or null for the line that asks them to agree one with Adam. Never a rule, a limit or a
// percentage. The heading starts with the level's word (member 3) and the questions follow what slipped (member 16).
// carried: the actions carried into this month and still not done (carriedFromLog, or the home's own rows), named
// (member 6). logHref: the log's page, for "See which in your log" to the first month with an action not done, from
// the home only (the log's page has its months under the card). A level Adam set by hand is the card's level, with
// nothing to say so (levels.js withSet): outside its own band of slips its heading names no count (REFOCUS.headingSet),
// and with nothing slipped there are no facts.
export function refocusView(log, goal, { carried = null, logHref = null } = {}) {
  if (!log) return null
  const level = accountability(log).level
  if (!REFOCUS.heading[level.key]) return null
  const body = typeof goal?.body === 'string' ? goal.body.trim() : ''
  const named = (carried || carriedFromLog(log)).filter(b => typeof b === 'string' && b.trim())
  const month = level.notDone && logHref ? firstNotDoneMonth(log) : null
  return {
    key: level.key,
    heading: level.set && !level.fits && REFOCUS.headingSet[level.key]
      ? REFOCUS.headingSet[level.key]
      : typeof REFOCUS.heading[level.key] === 'function' ? REFOCUS.heading[level.key](level.slips) : REFOCUS.heading[level.key],
    goal: body ? REFOCUS.goal(body) : REFOCUS.noGoal,
    only: REFOCUS.only,
    facts: LOG.slipped(level) || null,
    carried: named.length ? LOG_MEMBER.carriedNamed(named) : null,
    which: month ? { href: `${logHref}#mlog-month-${month}`, label: LOG_MEMBER.seeWhich } : null,
    askH: REFOCUS.askH,
    asks: REFOCUS.asks[causeOf(level)] || REFOCUS.asks.both,
    help: level.key === 'a_little_behind' ? null : REFOCUS.help,
  }
}

// The next session card: { date, line, join, calendar, today, all } or null. The time from its start to its end, as the
// session's own length gives it (member 14); today: it's on today and hasn't ended, so on a phone it leads the home
// until it ends (question 4); all: the Sessions page
export function nextSessionView({ product, sessions, groups, nowMs }) {
  const s = nextSession(sessions, nowMs)
  if (!s) return null
  const start = Date.parse(s.starts_at)
  const end = start + (s.duration_minutes || 0) * MIN
  const joinAt = start - (product.join_opens_minutes ?? 15) * MIN
  const open = nowMs >= joinAt && nowMs < end
  const group = s.cohort_id ? (groups || []).find(g => g.id === s.cohort_id)?.name || null : COPY.everyGroup
  const href = zoomLink(s.zoom_join_url)
  const when = end > start ? COPY.fromTo(f.time(start), f.time(end)) : f.time(start)
  return {
    id: s.id,
    date: f.longDate(start),
    line: group ? COPY.onZoom(when, group) : `${when} on Zoom`,
    today: f.daysFrom(nowMs, start) === 0,
    all: `${product.path || ''}/sessions`,
    // before it opens, when it opens; once open, the button (with no link typed in yet, neither)
    join: open ? (href ? { open: true, href, label: COPY.join } : null) : { open: false, label: COPY.joinOpensAt(f.time(joinAt)) },
    calendar: { uid: s.id, title: `${product.name}, ${s.title}`, startMs: start, endMs: end, path: product.path || '/' },
  }
}

// "Due at your session on Sun 25 Oct.", under this month's count: their group's next session (member 6), or null
export function dueLine(sessions, nowMs) {
  const s = nextSession(sessions, nowMs)
  return s ? COPY.dueAt(f.shortDate(s.starts_at)) : null
}

// A cycle's line: "Month 3, set at the session on Sun 27 Sep"
function cycleLine(cycle, sessions) {
  const s = cycle.session_id ? (sessions || []).find(x => x.id === cycle.session_id) : null
  return s?.starts_at ? COPY.setAt(cycle.month, f.shortDate(s.starts_at)) : COPY.monthOnly(cycle.month)
}

// This month's and the past months' actions:
// { open: { cycle, line, count, rows } | null, past: [{ cycle, line, count, rows }] }. count: "1 of 5 ticked", since a
// tick doesn't settle an action, Adam's review does (member 7, 20 Oct).
// Each row: { id, body, done, doneAt, note, carriedFrom, carriedTo }
export function cyclesView(cycles, actions, sessions) {
  const list = (actions || []).slice().sort((a, b) => a.sort_order - b.sort_order || String(a.id).localeCompare(String(b.id)))
  const monthOfCycle = new Map((cycles || []).map(c => [c.id, c.month]))
  const monthOfAction = new Map(list.map(a => [a.id, monthOfCycle.get(a.cycle_id)]))
  const carriedTo = new Map(list.filter(a => a.carried_over_from_id).map(a => [a.carried_over_from_id, monthOfCycle.get(a.cycle_id)]))
  const view = cycle => {
    const rows = list.filter(a => a.cycle_id === cycle.id).map(a => {
      const fromMonth = a.carried_over_from_id ? monthOfAction.get(a.carried_over_from_id) : null
      return {
        id: a.id,
        body: a.body,
        done: Boolean(a.done_at),
        doneAt: a.done_at || null,
        note: a.member_note || '',
        carriedFrom: a.carried_over_from_id ? (fromMonth ? COPY.carriedFrom(fromMonth) : COPY.carried) : null,
        carriedTo: carriedTo.has(a.id) && carriedTo.get(a.id) ? COPY.carriedTo(carriedTo.get(a.id)) : null,
      }
    })
    return { cycle, line: cycleLine(cycle, sessions), count: COPY.done(rows.filter(r => r.done).length, rows.length), rows }
  }
  const sorted = (cycles || []).slice().sort((a, b) => b.month - a.month)
  const open = sorted.find(c => !c.closed_at) || null
  return { open: open ? view(open) : null, past: sorted.filter(c => c.closed_at).map(view) }
}

// The line for the database's refusal of a tick or a note, or the general one
export function actionError(error) {
  if (error?.message === 'action_closed') return COPY.actionClosed
  return null
}

// Today in London, as the database keeps win_date: '2026-09-30'
export function londonDay(nowMs) {
  const u = f.uk(nowMs)
  return `${u.y}-${String(u.m).padStart(2, '0')}-${String(u.d).padStart(2, '0')}`
}

// The longest "More about it" the database keeps (hub.wins.detail's check, 2,000 since migration 20261011120000);
// the form holds to the same
export const WIN_DETAIL_MAX = LIMITS.winDetail

// The wins, newest first: [{ id, title, meta, detail, category, shareable, memberLogged }]. detail: its
// "More about it", '' for none. memberLogged: the member logged it (created_by_kind 'member'), so it's
// theirs to change and delete; on a win staff logged (or an import brought in) the database lets them
// change only the share tick (migration 20261007120000, 42501 win_logged_by_staff)
export function winsView(wins, nowMs) {
  const today = londonDay(nowMs)
  return (wins || []).slice()
    .sort((a, b) => String(b.win_date).localeCompare(String(a.win_date)) || String(b.created_at || '').localeCompare(String(a.created_at || '')))
    .map(w => ({
      id: w.id,
      title: w.title,
      meta: COPY.winMeta(categoryName(w.category), w.win_date === today ? COPY.today : f.dayMonth(f.calendarDay(w.win_date))),
      detail: typeof w.detail === 'string' ? w.detail.trim() : '',
      category: w.category,
      shareable: Boolean(w.shareable),
      memberLogged: w.created_by_kind === 'member',
    }))
}

// The line for the database's refusal of a win, or the general one: its more about it (wins_detail_check)
// or its title (wins_title_check)
export function winError(error) {
  if (error?.code !== '23514') return null
  return /detail/.test(error.message || '') ? COPY.winDetailLength(WIN_DETAIL_MAX) : COPY.winTitle
}

// The directory's cards, by name, as the database orders them: the whole Accelerator, every group
// (Adam's answer 1 of 7 Oct), so each card says which group they're in (none for someone not in 1)
export function directoryView(rows) {
  return (rows || []).map(r => ({
    id: r.person_id,
    name: r.full_name,
    initials: f.initials(r.full_name),
    group: r.cohort_name || '',
    location: r.location || '',
    about: r.about || '',
    hasPhoto: Boolean(r.has_photo),
    me: Boolean(r.is_me),
  }))
}
